import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { computed, effect, signal } from '@preact/signals-core';
import type { TemplateResult } from 'lit';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import {
  CanvasViewportParticipantController,
  type CanvasViewportRowAnchorRect,
  type CanvasViewportTargetPosition,
  createElementCanvasViewportTarget,
  createRowAnchoredCanvasViewportTarget,
} from '../../../core/canvas-viewport.js';
import { dv } from '../../../core/common/dv-css.js';
import {
  type GroupTrait,
  groupTraitKey,
} from '../../../core/group-by/trait.js';
import { createUniComponentFromWebComponent } from '../../../core/index.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
} from '../../../core/view/data-view-base.js';
import {
  dataViewBodyStateValue,
  measureDataViewBodyIntrinsicHeight,
} from '../../../core/view/data-view-body.js';
import {
  type TableSingleView,
  TableViewRowSelection,
  type TableViewSelectionWithType,
} from '../../index.js';
import { TABLE_ROW_HEIGHT } from '../consts.js';
import { handleTableWheel, wheelDeltaToPixels } from '../utils/wheel.js';
import { TableClipboardController } from './controller/clipboard.js';
import { TableDragController } from './controller/drag.js';
import { TableHotkeysController } from './controller/hotkeys.js';
import { TableSelectionController } from './controller/selection.js';
import { TableGroupFooter } from './group/bottom/group-footer.js';
import { TableGroupHeader } from './group/top/group-header.js';
import {
  createVirtualTableLayoutSnapshot,
  reconcileTableSelectionAfterColumnChange,
} from './layout-snapshot.js';
import { DatabaseCellContainer } from './row/cell.js';
import { TableRowHeader } from './row/row-header.js';
import { TableRowLast } from './row/row-last.js';
import * as styles from './table-view-css.js';
import type {
  TableCellData,
  TableGrid,
  TableGroupData,
  TableRowData,
} from './types.js';
import { GridVirtualScroll } from './virtual/virtual-scroll.js';

const TABLE_CANVAS_X_SCROLL_TARGET = 'table-x';
const TABLE_CANVAS_Y_SCROLL_TARGET = 'table-y';
const RELATION_CELL_MAX_VISIBLE_ENTRIES = 10;
const RELATION_CELL_LINE_HEIGHT = 24;
const RELATION_CELL_MORE_COUNT_HEIGHT = 18;
const RELATION_CELL_LINE_GAP = 4;
const RELATION_CELL_VERTICAL_PADDING = 4;
const CANVAS_VIEWPORT_TARGET_CHANGE_QUIET_MS = 120;
const TABLE_LAYOUT_CACHE_WRITE_DELAY_MS = 200;

function estimateRelationCellHeight(value: unknown): number {
  if (!Array.isArray(value) || value.length === 0) {
    return TABLE_ROW_HEIGHT;
  }
  const visibleCount = Math.min(
    value.length,
    RELATION_CELL_MAX_VISIBLE_ENTRIES
  );
  const hasMoreCount = value.length > visibleCount;
  const lineCount = visibleCount + (hasMoreCount ? 1 : 0);
  return Math.max(
    TABLE_ROW_HEIGHT,
    RELATION_CELL_VERTICAL_PADDING +
      visibleCount * RELATION_CELL_LINE_HEIGHT +
      (hasMoreCount ? RELATION_CELL_MORE_COUNT_HEIGHT : 0) +
      Math.max(0, lineCount - 1) * RELATION_CELL_LINE_GAP
  );
}

function createTableLayoutSignature(view: TableSingleView): string {
  return JSON.stringify({
    columns: view.properties$.value.map(property => ({
      id: property.id,
      type: property.type$.value,
      width: property.width$.value,
    })),
    cellDisplayMode: view.cellDisplayMode$.value,
  });
}

export class VirtualTableViewUILogic extends DataViewUILogicBase<
  TableSingleView,
  TableViewSelectionWithType
> {
  ui$ = signal<TableViewUI | undefined>();
  clipboardController = new TableClipboardController(this);
  dragController = new TableDragController(this);
  hotkeysController = new TableHotkeysController(this);
  selectionController = new TableSelectionController(this);

  virtualScroll$ = signal<TableGrid>();

  tableViewportWidth$ = signal(0);

  layoutSnapshot$ = computed(() =>
    createVirtualTableLayoutSnapshot(
      this.view.properties$.value,
      this.tableViewportWidth$.value
    )
  );

  columns$ = computed(() => this.layoutSnapshot$.value.gridColumns);

  groupTrait$ = computed(() => {
    return this.view.traitGet(groupTraitKey);
  });

  groups$ = computed(() => {
    const groupTrait = this.groupTrait$.value;
    if (!groupTrait?.groupsDataList$.value) {
      return [
        {
          id: '',
          rows: this.view.getBoundedRowIdsWindow().rowIds,
        },
      ];
    }
    const groups = groupTrait.groupsDataList$.value.filter(
      (
        group
      ): group is NonNullable<
        (typeof groupTrait.groupsDataList$.value)[number]
      > => group != null
    );
    const visibleRowIds = new Set(this.view.getBoundedRowIdsWindow().rowIds);
    return groups.map(group => ({
      id: group.key,
      rows: group.rows
        .map(v => v.rowId)
        .filter(rowId => visibleRowIds.has(rowId)),
    }));
  });

  clearSelection = () => {
    this.selectionController.clear();
  };

  addRow = (position: InsertToPosition) => {
    if (!this.view.rowCanAdd()) {
      const reason = this.view.rowAddDisabledReason();
      if (reason) this.root.config.notification.toast(reason);
      return;
    }
    return this.view.rowAdd(position);
  };

  focusFirstCell = () => {
    this.selectionController.focusFirstCell();
  };

  showIndicator = (evt: MouseEvent) => {
    return this.dragController.showIndicator(evt) != null;
  };

  hideIndicator = () => {
    this.dragController.dropPreview.remove();
  };

  moveTo = (id: string, evt: MouseEvent) => {
    const result = this.dragController.getInsertPosition(evt);
    if (result) {
      const row = this.view.rowGetOrCreate(id);
      row.move(result.position, undefined, result.groupKey);
    }
  };

  onWheel = (event: WheelEvent) => {
    handleTableWheel(event);
  };

  renderAddGroup = (groupHelper: GroupTrait) => {
    const addGroup = groupHelper.addGroup;
    if (!addGroup) {
      return;
    }
    const add = (e: MouseEvent) => {
      const ele = e.currentTarget as HTMLElement;
      popMenu(popupTargetFromElement(ele), {
        options: {
          preferDesktop: true,
          items: [
            menu.input({
              onComplete: text => {
                const column = groupHelper.property$.value;
                if (column) {
                  column.dataUpdate(() =>
                    addGroup({
                      text,
                      oldData: column.data$.value,
                      dataSource: this.view.manager.dataSource,
                    })
                  );
                }
              },
            }),
          ],
        },
      });
    };
    return html` <div style="display:flex;">
      <div class="${dv.hover} ${dv.round8} ${styles.addGroup}" @click="${add}">
        <div class="${dv.icon16}" style="display:flex;">${AddCursorIcon()}</div>
        <div>New Group</div>
      </div>
    </div>`;
  };

  initVirtualScroll(
    scrollContainers: {
      xScrollContainer: HTMLElement;
      yScrollContainer: HTMLElement;
    },
    ui: TableViewUI
  ) {
    if (this.virtualScroll$.value) {
      return;
    }
    const tableLayoutSignature = createTableLayoutSignature(this.view);
    const initialLayoutState =
      this.view.manager.dataSource.getTableViewLayoutState(
        this.view.id,
        tableLayoutSignature
      );
    ui.setCachedIntrinsicContentHeight(
      initialLayoutState?.intrinsicContentHeight
    );
    const initialRowHeights = initialLayoutState
      ? new Map(Object.entries(initialLayoutState.rowHeights))
      : undefined;
    let pendingCachedRowHeights = initialRowHeights
      ? new Map(initialRowHeights)
      : new Map<string, number>();
    let layoutCacheWriteTimer: ReturnType<typeof setTimeout> | undefined;
    let lastWrittenTableLayoutSnapshot: string | undefined;
    let virtualScroll!: GridVirtualScroll<
      TableGroupData,
      TableRowData,
      TableCellData
    >;
    const writeTableLayoutCache = () => {
      const intrinsicContentHeight = virtualScroll.lastGroupBottom$.value;
      const rowHeights = Object.fromEntries(pendingCachedRowHeights);
      if (
        intrinsicContentHeight == null &&
        Object.keys(rowHeights).length === 0
      ) {
        return;
      }
      const snapshot = JSON.stringify({
        intrinsicContentHeight,
        rowHeights,
      });
      if (snapshot === lastWrittenTableLayoutSnapshot) {
        return;
      }
      lastWrittenTableLayoutSnapshot = snapshot;
      this.view.manager.dataSource.setTableViewLayoutState(this.view.id, {
        columnLayoutSignature: tableLayoutSignature,
        intrinsicContentHeight,
        measuredAt: Date.now(),
        rowHeights,
        version: 1,
      });
    };
    const scheduleTableLayoutCacheWrite = () => {
      if (layoutCacheWriteTimer != null) {
        clearTimeout(layoutCacheWriteTimer);
      }
      layoutCacheWriteTimer = setTimeout(() => {
        layoutCacheWriteTimer = undefined;
        writeTableLayoutCache();
      }, TABLE_LAYOUT_CACHE_WRITE_DELAY_MS);
    };
    virtualScroll = new GridVirtualScroll<
      TableGroupData,
      TableRowData,
      TableCellData
    >({
      initGroupData: group => ({
        hover$: computed(() => {
          const headerHover = group.data.headerHover$.value;
          if (headerHover) {
            return true;
          }
          const footerHover = group.data.footerHover$.value;
          if (footerHover) {
            return true;
          }
          return group.rows$.value.some(row => row.data.hover$.value);
        }),
        headerHover$: signal(false),
        footerHover$: signal(false),
      }),
      initRowData: row => ({
        hover$: computed(() => {
          return row.cells$.value.some(cell => cell.data.hover$.value);
        }),
        selected$: computed(() => {
          const selection = this.selection$.value;
          if (!selection || selection.selectionType !== 'row') {
            return false;
          }
          const groupId = row.group.groupId;
          return TableViewRowSelection.includes(selection, {
            id: row.rowId,
            groupKey: groupId ? groupId : undefined,
          });
        }),
      }),
      initCellData: () => ({
        hover$: signal(false),
        selected$: signal(false),
      }),
      columns$: this.columns$,
      groups$: this.groups$,
      createCell: cell => {
        if (cell.columnId === 'row-header') {
          const rowHeader = new TableRowHeader();
          rowHeader.gridCell = cell;
          rowHeader.tableViewLogic = this;
          return rowHeader;
        }
        if (cell.columnId === 'row-last') {
          const rowLast = new TableRowLast();
          rowLast.gridCell = cell;
          rowLast.tableViewLogic = this;
          return rowLast;
        }
        const cellContainer = new DatabaseCellContainer();
        cellContainer.gridCell = cell;
        cellContainer.tableViewLogic = this;
        return cellContainer;
      },
      createGroup: {
        top: gridGroup => {
          const groupHeader = new TableGroupHeader();
          groupHeader.tableViewLogic = this;
          groupHeader.gridGroup = gridGroup;
          return groupHeader;
        },
        bottom: gridGroup => {
          const groupFooter = new TableGroupFooter();
          groupFooter.tableViewLogic = this;
          groupFooter.gridGroup = gridGroup;
          return groupFooter;
        },
      },
      fixedRowHeight$: signal(undefined),
      estimatedRowHeight$: signal(TABLE_ROW_HEIGHT),
      initialRowHeights,
      onRowHeightsChange: rowHeights => {
        pendingCachedRowHeights = new Map(rowHeights);
        ui.queueCanvasViewportTargetsChanged();
        scheduleTableLayoutCacheWrite();
      },
      xScrollContainer: scrollContainers.xScrollContainer,
      yScrollContainer: scrollContainers.yScrollContainer,
    });
    this.virtualScroll$.value = virtualScroll;
    let previousPropertyIds = this.layoutSnapshot$.peek().propertyIds;
    const stopLayoutReconciliation = effect(() => {
      const nextPropertyIds = this.layoutSnapshot$.value.propertyIds;
      if (
        previousPropertyIds.length === nextPropertyIds.length &&
        previousPropertyIds.every((id, index) => id === nextPropertyIds[index])
      ) {
        return;
      }
      const selection = reconcileTableSelectionAfterColumnChange(
        this.selectionController.selection,
        previousPropertyIds,
        nextPropertyIds
      );
      if (selection !== this.selectionController.selection) {
        this.selectionController.selection = selection;
      }
      previousPropertyIds = nextPropertyIds;
      ui.queueHorizontalScrollReconciliation();
    });
    ui.disposables.add(stopLayoutReconciliation);
    requestAnimationFrame(() => {
      if (virtualScroll) {
        virtualScroll.init();
        const stopIntrinsicHeightEffect = effect(() => {
          virtualScroll.lastGroupBottom$.value;
          ui.queueIntrinsicContentHeightChange();
          scheduleTableLayoutCacheWrite();
        });
        type RelationHeightGroup = (typeof virtualScroll.groups$.value)[number];
        const relationHeightEffects = new Map<
          RelationHeightGroup,
          Map<string, () => void>
        >();
        const stopRelationHeightStructureEffect = effect(() => {
          const relationProperties = this.view.properties$.value.filter(
            property => property.type$.value === 'relation'
          );
          const activeGroups = new Set<RelationHeightGroup>();
          for (const group of virtualScroll.groups$.value) {
            activeGroups.add(group);
            const groupEffects =
              relationHeightEffects.get(group) ?? new Map<string, () => void>();
            relationHeightEffects.set(group, groupEffects);
            const activeKeys = new Set<string>();
            for (const rowId of group.rowIds$.value) {
              for (const property of relationProperties) {
                const key = `${rowId}\u0000${property.id}`;
                activeKeys.add(key);
                if (!groupEffects.has(key)) {
                  groupEffects.set(
                    key,
                    effect(() => {
                      const value = this.view.cellGetOrCreate(
                        rowId,
                        property.id
                      ).value$.value;
                      group.updateCellHeight(
                        rowId,
                        property.id,
                        estimateRelationCellHeight(value),
                        {
                          allowCachedShrink: true,
                          preserveRestoredRowHeight: true,
                        }
                      );
                    })
                  );
                }
              }
            }
            for (const [key, dispose] of groupEffects) {
              if (!activeKeys.has(key)) {
                dispose();
                groupEffects.delete(key);
              }
            }
          }
          for (const [group, groupEffects] of relationHeightEffects) {
            if (!activeGroups.has(group)) {
              groupEffects.forEach(dispose => dispose());
              relationHeightEffects.delete(group);
            }
          }
        });
        ui.disposables.add(() => {
          if (layoutCacheWriteTimer != null) {
            clearTimeout(layoutCacheWriteTimer);
            layoutCacheWriteTimer = undefined;
          }
          writeTableLayoutCache();
          stopIntrinsicHeightEffect();
          stopRelationHeightStructureEffect();
          relationHeightEffects.forEach(groupEffects =>
            groupEffects.forEach(dispose => dispose())
          );
          relationHeightEffects.clear();
          virtualScroll.dispose();
          if (this.virtualScroll$.value === virtualScroll) {
            this.virtualScroll$.value = undefined;
          }
        });
      }
    });
  }

  renderer = createUniComponentFromWebComponent(TableViewUI);
}

export class TableViewUI extends DataViewUIBase<VirtualTableViewUILogic> {
  private readonly yScrollContainerRef: Ref<HTMLDivElement> = createRef();

  private readonly xScrollContainerRef: Ref<HTMLDivElement> = createRef();

  /**
   * Horizontal scroll position of the table body. Public so the sticky
   * header can follow programmatic scrolling (column-drag edge auto-scroll).
   */
  readonly xScrollLeft$ = signal(0);

  private readonly scrollContentWidth$ = signal(0);

  private readonly scrollViewportWidth$ = signal(0);

  private cachedIntrinsicContentHeight: number | undefined;

  private tableViewportResizeObserver: ResizeObserver | undefined;

  private canvasViewportTargetsChangedTimer:
    | ReturnType<typeof setTimeout>
    | undefined;

  private virtualScrollRenderQueued = false;

  private horizontalScrollReconciliationFrame: number | undefined;

  private readonly canvasViewport = new CanvasViewportParticipantController(
    this,
    'table',
    () => this.logic.view.featureFlags$.value.use_shared_canvas_viewport
  );

  private renderTable() {
    return this.logic.virtualScroll$.peek()?.content;
  }

  setCachedIntrinsicContentHeight(height: number | undefined): void {
    this.cachedIntrinsicContentHeight =
      typeof height === 'number' && Number.isFinite(height) && height > 0
        ? height
        : undefined;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.logic.ui$.value = this;
    this.logic.clipboardController.hostConnected();
    this.logic.dragController.hostConnected();
    this.logic.hotkeysController.hostConnected();
    this.logic.selectionController.hostConnected();
    this.classList.add(styles.tableView);
    if (this.logic.view.featureFlags$.value.use_shared_canvas_viewport) {
      this.dataset.canvasViewportParticipant = 'table';
    }
  }

  override disconnectedCallback(): void {
    this.tableViewportResizeObserver?.disconnect();
    this.tableViewportResizeObserver = undefined;
    if (this.canvasViewportTargetsChangedTimer != null) {
      clearTimeout(this.canvasViewportTargetsChangedTimer);
      this.canvasViewportTargetsChangedTimer = undefined;
    }
    this.canvasViewport.dispose();
    if (this.horizontalScrollReconciliationFrame != null) {
      cancelAnimationFrame(this.horizontalScrollReconciliationFrame);
      this.horizontalScrollReconciliationFrame = undefined;
    }
    super.disconnectedCallback();
  }

  protected override updated(): void {
    if (this.ensureVirtualScroll()) {
      this.queueVirtualScrollRender();
    }
    this.syncDataViewBodyResizeObserver({
      body: this.yScrollContainerRef.value,
      extraTargets: [
        this.xScrollContainerRef.value,
        this.logic.virtualScroll$.value?.content,
      ],
      observeBodyChildren: false,
    });
    this.syncCanvasViewportParticipants();
    this.queueIntrinsicContentHeightChange();
    const xScrollContainer = this.xScrollContainerRef.value;
    if (xScrollContainer) {
      this.scrollViewportWidth$.value = xScrollContainer.clientWidth;
      this.scrollContentWidth$.value = xScrollContainer.scrollWidth;
    }
  }

  private ensureVirtualScroll(): boolean {
    const xScrollContainer = this.xScrollContainerRef.value;
    const yScrollContainer = this.yScrollContainerRef.value;
    if (
      !xScrollContainer ||
      !yScrollContainer ||
      this.logic.virtualScroll$.peek()
    ) {
      return false;
    }
    this.observeTableViewportWidth(xScrollContainer);
    this.logic.initVirtualScroll(
      {
        xScrollContainer,
        yScrollContainer,
      },
      this
    );
    return true;
  }

  private queueVirtualScrollRender(): void {
    if (this.virtualScrollRenderQueued) {
      return;
    }
    this.virtualScrollRenderQueued = true;
    queueMicrotask(() => {
      this.virtualScrollRenderQueued = false;
      if (this.isConnected) {
        this.requestUpdate();
      }
    });
  }

  private observeTableViewportWidth(container: HTMLElement): void {
    const syncWidth = () => {
      this.logic.tableViewportWidth$.value = container.clientWidth;
      this.scrollViewportWidth$.value = container.clientWidth;
      this.scrollContentWidth$.value = container.scrollWidth;
    };
    syncWidth();
    this.tableViewportResizeObserver?.disconnect();
    this.tableViewportResizeObserver = new ResizeObserver(syncWidth);
    this.tableViewportResizeObserver.observe(container);
  }

  applyCanvasViewportTarget(
    targetId: string,
    position: CanvasViewportTargetPosition
  ): boolean {
    return this.canvasViewport.applyTarget(targetId, position);
  }

  getCanvasViewportTargets(): Record<string, CanvasViewportTargetPosition> {
    return this.canvasViewport.getTargets();
  }

  queueCanvasViewportTargetsChanged(): void {
    if (this.canvasViewportTargetsChangedTimer != null) {
      clearTimeout(this.canvasViewportTargetsChangedTimer);
    }
    this.canvasViewportTargetsChangedTimer = setTimeout(() => {
      this.canvasViewportTargetsChangedTimer = undefined;
      this.canvasViewport.notifyTargetsChanged();
    }, CANVAS_VIEWPORT_TARGET_CHANGE_QUIET_MS);
  }

  queueHorizontalScrollReconciliation(): void {
    if (this.horizontalScrollReconciliationFrame != null) {
      cancelAnimationFrame(this.horizontalScrollReconciliationFrame);
    }
    this.horizontalScrollReconciliationFrame = requestAnimationFrame(() => {
      this.horizontalScrollReconciliationFrame = undefined;
      const container = this.xScrollContainerRef.value;
      if (!container) {
        return;
      }
      const maxScrollLeft = Math.max(
        0,
        container.scrollWidth - container.clientWidth
      );
      const nextScrollLeft = Math.min(container.scrollLeft, maxScrollLeft);
      if (container.scrollLeft !== nextScrollLeft) {
        container.scrollLeft = nextScrollLeft;
      }
      this.xScrollLeft$.value = nextScrollLeft;
    });
  }

  private syncCanvasViewportParticipants(): void {
    this.canvasViewport.sync([
      createElementCanvasViewportTarget(
        TABLE_CANVAS_X_SCROLL_TARGET,
        this.xScrollContainerRef.value,
        'x'
      ),
      createRowAnchoredCanvasViewportTarget(
        TABLE_CANVAS_Y_SCROLL_TARGET,
        this.yScrollContainerRef.value,
        'y',
        () => this.getTableRowAnchorRects()
      ),
    ]);
  }

  private getTableRowAnchorRects(): CanvasViewportRowAnchorRect[] {
    const grid = this.logic.virtualScroll$.value;
    if (!grid) {
      return [];
    }
    const rows: CanvasViewportRowAnchorRect[] = [];
    for (const group of grid.groups$.value) {
      for (const row of group.rows$.value) {
        const top = row.top$.value;
        const height = row.height$.value;
        if (top == null || height == null) {
          continue;
        }
        rows.push({
          height,
          rowId: row.rowId,
          top,
        });
      }
    }
    return rows;
  }

  override getIntrinsicContentHeight(visualScale = 1): number {
    const tableContainer = this.yScrollContainerRef.value;
    const gridHeight = this.logic.virtualScroll$.value?.lastGroupBottom$.value;
    // Canvas autosize must not depend on scrollHeight: in edgeless mode the
    // scroll container height is itself driven by the canvas block height.
    // Before the virtual grid has produced row metrics, scrollHeight is still
    // useful as a one-way bootstrap value. Once gridHeight exists, it is the
    // only data height source so host height cannot feed back into itself.
    const dataHeight =
      gridHeight ??
      this.cachedIntrinsicContentHeight ??
      tableContainer?.scrollHeight ??
      0;
    const scale = visualScale || 1;
    const bodyHeight = measureDataViewBodyIntrinsicHeight({
      body: tableContainer,
      visualScale: scale,
    });
    return Math.max(dataHeight, bodyHeight);
  }

  private readonly onHorizontalScroll = (event: Event) => {
    const target = event.currentTarget;
    if (target instanceof HTMLElement) {
      this.xScrollLeft$.value = target.scrollLeft;
      this.scrollContentWidth$.value = target.scrollWidth;
      this.scrollViewportWidth$.value = target.clientWidth;
    }
  };

  /**
   * Viewport of the horizontal scroll container in client coordinates. The
   * sticky column header lives outside that container, so header drags use
   * this rect to stay inside the visible database block.
   */
  getHorizontalViewportRect(): DOMRect | undefined {
    return this.xScrollContainerRef.value?.getBoundingClientRect();
  }

  /**
   * Scroll the table horizontally by `delta` pixels, clamped to the content
   * range. Keeps the sticky header in sync through `xScrollLeft$`.
   *
   * @returns the applied scroll distance
   */
  scrollHorizontallyBy(delta: number): number {
    const xScrollContainer = this.xScrollContainerRef.value;
    if (!xScrollContainer) {
      return 0;
    }
    const maxScrollLeft =
      xScrollContainer.scrollWidth - xScrollContainer.clientWidth;
    if (maxScrollLeft <= 0) {
      return 0;
    }
    const nextScrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, xScrollContainer.scrollLeft + delta)
    );
    const applied = nextScrollLeft - xScrollContainer.scrollLeft;
    if (applied === 0) {
      return 0;
    }
    xScrollContainer.scrollLeft = nextScrollLeft;
    this.xScrollLeft$.value = nextScrollLeft;
    return applied;
  }

  private readonly onHorizontalWheel = (event: WheelEvent) => {
    if (event.metaKey || event.ctrlKey) {
      return;
    }

    const xScrollContainer = this.xScrollContainerRef.value;
    if (!xScrollContainer) {
      return;
    }

    const maxScrollLeft =
      xScrollContainer.scrollWidth - xScrollContainer.clientWidth;
    if (maxScrollLeft <= 0) {
      return;
    }

    const rawDeltaX = event.deltaX;
    const rawDeltaY = event.deltaY;
    const deltaX =
      event.shiftKey && Math.abs(rawDeltaY) > Math.abs(rawDeltaX)
        ? rawDeltaY
        : rawDeltaX;
    const deltaY = event.shiftKey ? 0 : rawDeltaY;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    const delta = wheelDeltaToPixels(
      deltaX,
      event.deltaMode,
      xScrollContainer.clientWidth
    );

    event.preventDefault();
    event.stopPropagation();
    this.scrollHorizontallyBy(delta);
  };

  private readonly onHorizontalScrollbarKeyDown = (event: KeyboardEvent) => {
    const container = this.xScrollContainerRef.value;
    if (!container) return;
    const step = event.key === 'PageDown' || event.key === 'PageUp' ? 0.8 : 0.1;
    const direction =
      event.key === 'ArrowRight' || event.key === 'PageDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'PageUp'
          ? -1
          : 0;
    if (direction === 0) return;
    event.preventDefault();
    container.scrollLeft += direction * container.clientWidth * step;
    this.xScrollLeft$.value = container.scrollLeft;
  };

  private readonly onHorizontalScrollbarPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    const container = this.xScrollContainerRef.value;
    const track = event.currentTarget;
    if (!container || !(track instanceof HTMLElement)) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) return;

    const trackRect = track.getBoundingClientRect();
    const thumbWidth = Math.max(
      32,
      (container.clientWidth / container.scrollWidth) * trackRect.width
    );
    const maxThumbLeft = Math.max(1, trackRect.width - thumbWidth);
    const pointerToScrollLeft = (clientX: number) =>
      (Math.max(
        0,
        Math.min(maxThumbLeft, clientX - trackRect.left - thumbWidth / 2)
      ) /
        maxThumbLeft) *
      maxScrollLeft;
    const startPointerX = event.clientX;
    const startScrollLeft = container.scrollLeft;
    const startedOnThumb =
      event.clientX - trackRect.left >=
        (startScrollLeft / maxScrollLeft) * maxThumbLeft &&
      event.clientX - trackRect.left <=
        (startScrollLeft / maxScrollLeft) * maxThumbLeft + thumbWidth;

    if (!startedOnThumb) {
      container.scrollLeft = pointerToScrollLeft(event.clientX);
      this.xScrollLeft$.value = container.scrollLeft;
    }
    const dragStartScrollLeft = container.scrollLeft;
    const dragStartPointerX = startedOnThumb ? startPointerX : event.clientX;

    const onPointerMove = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientX - dragStartPointerX;
      container.scrollLeft = Math.max(
        0,
        Math.min(
          maxScrollLeft,
          dragStartScrollLeft + (delta / maxThumbLeft) * maxScrollLeft
        )
      );
      this.xScrollLeft$.value = container.scrollLeft;
    };
    const stopDragging = () => {
      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', stopDragging);
      track.removeEventListener('pointercancel', stopDragging);
    };

    event.preventDefault();
    track.setPointerCapture(event.pointerId);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', stopDragging);
    track.addEventListener('pointercancel', stopDragging);
  };

  private renderHorizontalScrollbar() {
    const viewportWidth = this.scrollViewportWidth$.value;
    const contentWidth = this.scrollContentWidth$.value;
    if (viewportWidth <= 0 || contentWidth <= viewportWidth) return;
    const maxScrollLeft = contentWidth - viewportWidth;
    const thumbWidth = Math.max(
      32,
      (viewportWidth / contentWidth) * viewportWidth
    );
    const maxThumbLeft = Math.max(0, viewportWidth - thumbWidth);
    const thumbLeft =
      maxScrollLeft > 0
        ? (this.xScrollLeft$.value / maxScrollLeft) * maxThumbLeft
        : 0;
    return html`<div
      class="${styles.horizontalScrollbar}"
      role="scrollbar"
      aria-label="Horizontal table scroll"
      aria-orientation="horizontal"
      aria-valuemin="0"
      aria-valuemax="${maxScrollLeft}"
      aria-valuenow="${this.xScrollLeft$.value}"
      tabindex="0"
      @keydown="${this.onHorizontalScrollbarKeyDown}"
      @pointerdown="${this.onHorizontalScrollbarPointerDown}"
    >
      <div
        class="${styles.horizontalScrollbarThumb}"
        style="${styleMap({
          transform: `translate3d(${thumbLeft}px, 0, 0)`,
          width: `${thumbWidth}px`,
        })}"
      ></div>
    </div>`;
  }

  override render(): TemplateResult {
    const vPadding = this.logic.root.config.virtualPadding$.value;
    const stickyHeaderStyle = styleMap({
      paddingLeft: `${vPadding}px`,
      paddingRight: `${vPadding}px`,
    });
    const stickyHeaderInnerStyle = styleMap({
      transform: `translate3d(${-this.xScrollLeft$.value}px, 0, 0)`,
    });
    const wrapperStyle = styleMap({
      marginLeft: `-${vPadding}px`,
      marginRight: `-${vPadding}px`,
    });
    const containerStyle = styleMap({
      paddingLeft: `${vPadding}px`,
      paddingRight: `${vPadding}px`,
      '--dv-horizontal-scroll-left': `${this.xScrollLeft$.value}px`,
    });
    return html`
      <div
        ${ref(this.yScrollContainerRef)}
        class="${styles.tableContainer}"
        data-data-view-body="true"
        data-data-view-body-state=${dataViewBodyStateValue('data')}
        style="${wrapperStyle}"
      >
        <div class="${styles.stickyColumnHeader}" style="${stickyHeaderStyle}">
          <div
            class="${styles.stickyColumnHeaderInner}"
            style="${stickyHeaderInnerStyle}"
            @wheel="${this.onHorizontalWheel}"
          >
            <virtual-table-header
              .tableViewLogic="${this.logic}"
            ></virtual-table-header>
          </div>
        </div>
        <div
          ${ref(this.xScrollContainerRef)}
          class="${styles.tableBlockTable}"
          data-data-view-body-content="true"
          data-data-view-body-intrinsic="true"
          data-data-view-body-render-root="true"
          @scroll="${this.onHorizontalScroll}"
          @wheel="${handleTableWheel}"
        >
          <div
            class="${styles.tableContainer2}"
            data-data-view-body-render-content="true"
            style="${containerStyle}"
          >
            ${this.renderTable()}
          </div>
        </div>
      </div>
      ${this.renderHorizontalScrollbar()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-table-view-ui-virtual': TableViewUI;
  }
}
